import React, { useState, useEffect } from 'react';
import { isValidVideoUrl, normalizeVideoUrl } from '../../utils/videoServiceDetector';
import videoMetadataService from '../../services/videoMetadataService';
import { formatDuration, formatPublishDate } from '../../utils/durationFormatter';

function AddVideoModal({ isOpen, onClose, onAdd }) {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [isUrlLocked, setIsUrlLocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Enhanced metadata from YouTube API
  const [metadata, setMetadata] = useState({
    duration: null,
    publishedAt: null,
    channelTitle: null,
    validationWarnings: []
  });

  useEffect(() => {
    if (url && !isUrlLocked) {
      handleUrlPaste();
    }
  }, [url]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUrlPaste = async () => {
    if (!url.trim()) return;

    // Normalize the URL (e.g., convert YouTube Shorts to standard watch format)
    const normalizedUrl = normalizeVideoUrl(url.trim());

    if (!isValidVideoUrl(normalizedUrl)) {
      setError('Please enter a valid video URL from YouTube, Vimeo, or Dailymotion');
      return;
    }

    // Update input with normalized URL
    setUrl(normalizedUrl);
    setError('');
    setIsUrlLocked(true);
    setLoading(true);

    try {
      const fetchedMetadata = await videoMetadataService.fetchMetadata(normalizedUrl);
      setTitle(fetchedMetadata.title || '');
      setThumbnailUrl(fetchedMetadata.thumbnailUrl || '');

      // Store enhanced metadata from YouTube API
      setMetadata({
        duration: fetchedMetadata.duration,
        publishedAt: fetchedMetadata.publishedAt,
        channelTitle: fetchedMetadata.channelTitle,
        validationWarnings: fetchedMetadata.validationWarnings || []
      });

      setLoading(false);
    } catch (err) {
      setLoading(false);
      const failedUrl = url;
      setError(`Failed to load video metadata\nURL: ${failedUrl}\nPlease check the URL and try again.`);
      handleReset();
    }
  };

  const handleReset = () => {
    setUrl('');
    setTitle('');
    setDescription('');
    setThumbnailUrl('');
    setIsUrlLocked(false);
    setLoading(false);
    setMetadata({
      duration: null,
      publishedAt: null,
      channelTitle: null,
      validationWarnings: []
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!url.trim() || !title.trim()) {
      return;
    }

    onAdd({
      url: url.trim(),
      title: title.trim(),
      description: description.trim(),
      thumbnailUrl: thumbnailUrl
    });

    handleReset();
    setError('');
    onClose();
  };

  const handleClose = () => {
    handleReset();
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-xl font-semibold text-gray-900">Add Video</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm whitespace-pre-line">
              {error}
            </div>
          )}

          {/* URL Field */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Video URL *
            </label>
            <div className="relative">
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={isUrlLocked}
                className={`w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:ring-primary-500 focus:border-transparent ${isUrlLocked ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''
                  }`}
                placeholder="https://youtube.com/watch?v=... or vimeo.com/... or dailymotion.com/..."
                autoFocus={!isUrlLocked}
              />
              {isUrlLocked && (
                <button
                  type="button"
                  onClick={handleReset}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Reset URL"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            {!isUrlLocked && (
              <p className="mt-1 text-xs text-gray-500">
                Paste a video URL from YouTube, Vimeo or Dailymotion
              </p>
            )}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="mb-4 flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
              <span className="ml-3 text-gray-600">Loading video metadata...</span>
            </div>
          )}

          {/* Metadata Fields (shown after successful fetch) */}
          {isUrlLocked && !loading && thumbnailUrl && (
            <>
              {/* Validation Warnings */}
              {metadata.validationWarnings.length > 0 && (
                <div className="mb-4 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded text-sm">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div>
                      {metadata.validationWarnings.map((warning, index) => (
                        <p key={index}>{warning}</p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Thumbnail Preview */}
              <div className="mb-4 relative">
                <img
                  src={thumbnailUrl}
                  alt="Video thumbnail"
                  className="w-full rounded-lg border border-gray-300"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                {/* Duration Badge */}
                {metadata.duration && (
                  <div className="absolute bottom-2 right-2 bg-black bg-opacity-80 text-white px-2 py-1 rounded text-xs font-semibold">
                    {formatDuration(metadata.duration)}
                  </div>
                )}
              </div>

              {/* Enhanced Metadata Info */}
              {(metadata.channelTitle || metadata.publishedAt) && (
                <div className="mb-4 text-sm text-gray-600 space-y-1">
                  {metadata.channelTitle && (
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                      </svg>
                      <span>{metadata.channelTitle}</span>
                    </div>
                  )}
                  {metadata.publishedAt && (
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                      </svg>
                      <span>Published {formatPublishDate(metadata.publishedAt)}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Title Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Video title"
                  required
                />
              </div>

              {/* Description Field */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (optional)
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter a description for this video"
                />
              </div>
            </>
          )}

          {/* Footer */}
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={!isUrlLocked || loading || !title.trim()}
              className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Video
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddVideoModal;
